import type { RegistrationData } from "@vidimo/shared";
import { SaveRegistrationResponseSchema } from "@vidimo/shared/contracts";
import { z } from "zod";
import { interfaceTranslator } from "@/i18n/translate";
import { refusalOf } from "@/lib/api-error-message";
import { apiJson } from "@/lib/api-request";

const AddScannedGuestResponseSchema = z.object({
  // Absent from an API that predates it, which never matched a document.
  alreadyOnBooking: z.boolean().default(false),
  guestId: z.string(),
  outcome: SaveRegistrationResponseSchema.shape.outcome,
});

/**
 * A registration route's machine-readable refusal, parsed off the error body:
 * the `code`, and for a document already registered on the stay, whose it is.
 * Server prose stays unrendered, per the `ApiRequestError.detail` contract;
 * the client owns the copy per code.
 */
const RefusalSchema = z.object({
  code: z.enum([
    "held_by_other",
    "not_on_booking",
    "party_full",
    "reservation_not_active",
  ]),
  holderName: z.string().optional(),
});

export type Refusal = z.infer<typeof RefusalSchema>;

/** The refusal carried by a registration route's rejection, or null. */
export function scanAddRefusal(error: unknown): Refusal | null {
  return refusalOf(error, RefusalSchema);
}

/**
 * What the host reads when the document is another guest's on this stay: the
 * number they entered, to check against the document in hand, and whose it is.
 */
export function heldByOtherMessage(
  documentNumber: string,
  holderName: string | undefined
): string {
  const t = interfaceTranslator("checkIn.guestRegistration");
  return t("heldByOther", {
    documentNumber: documentNumber.trim(),
    holderName: holderName || t("anotherGuest"),
  });
}

/**
 * What to say when a save landed but the filing still is not done.
 *
 * Keyed by the filing's error code, because "needs host action" is not one
 * situation: an authority that read the record and refused it needs different
 * words from a report Vidimo isn't filing, and telling a host their fix was
 * rejected sends them back to re-edit details that were never the problem.
 */
const SAVE_BLOCKED_KEYS: Record<
  string,
  | "savedBlockedAuth"
  | "savedBlockedFiledByHost"
  | "savedBlockedMissingData"
  | "savedBlockedNoConnector"
  | "savedBlockedOtherFacility"
  | "savedBlockedValidation"
> = {
  auth: "savedBlockedAuth",
  filed_by_host: "savedBlockedFiledByHost",
  missing_data: "savedBlockedMissingData",
  no_connector: "savedBlockedNoConnector",
  other_facility: "savedBlockedOtherFacility",
  // The one case where "they rejected it again" is the truth.
  validation: "savedBlockedValidation",
};

/** The message for a save that landed while the filing stayed blocked. */
export function saveBlockedMessage(
  errorCode: string | null | undefined
): string {
  const t = interfaceTranslator("shell.mrzScanner");
  return errorCode && Object.hasOwn(SAVE_BLOCKED_KEYS, errorCode)
    ? t(SAVE_BLOCKED_KEYS[errorCode])
    : t("savedBlockedUnknown");
}

// Client for the host-side registration write. A capture only becomes a
// *filable* registration when it reaches this route: it appends the
// `guest_registrations` snapshot the filing engine reads and resubmits the
// guest's due filings. A PowerSync write to `guests` alone leaves the filing
// stuck on "No reviewed registration data on file for this guest."

export type SaveRegistrationOutcome =
  | "filed"
  | "submitted"
  | "action_needed"
  | "none";

/** One reviewed identity capture, addressed to the guest it belongs to. */
export interface RegistrationCapture {
  data: RegistrationData;
  guestId: string;
  reservationId: string;
}

/**
 * Record a document scanned at the desk: on the person it was scanned for, or
 * on whoever holds it, or on someone new.
 *
 * An OTA sends a head count and one booker; the rest of the party has no
 * record until a host scans them, and this is where that row is created. The
 * server owns the whole write (guest row, occupant link, registration
 * snapshot) in one transaction under the booking's party lock, where it also
 * refuses a document another guest filed on this stay, so there is no
 * local-first path for it: a document saved to the wrong person is worse
 * than a scan that has to be retried online.
 */
export async function addScannedPartyMember({
  reservationId,
  data,
  manual,
  guestId,
  signal,
}: {
  reservationId: string;
  data: RegistrationData;
  /** True when the host typed the details instead of scanning them. */
  manual: boolean;
  /** Who the scan is for, when the host opened it from their row. */
  guestId?: string;
  signal?: AbortSignal;
}): Promise<{
  alreadyOnBooking: boolean;
  guestId: string;
  outcome: SaveRegistrationOutcome;
}> {
  return await apiJson(`/reservations/${reservationId}/guests`, {
    body: {
      capture: manual ? "manual" : "mrz_scan",
      guestId,
      registration: data,
    },
    label: "Add scanned guest",
    method: "POST",
    schema: AddScannedGuestResponseSchema,
    signal,
  });
}

/**
 * Correct a listed guest's capture for this stay. The server merges it onto
 * the stay's latest snapshot and refiles what is still due or was refused.
 */
export async function saveGuestRegistration({
  reservationId,
  guestId,
  data,
  signal,
}: RegistrationCapture & { signal?: AbortSignal }): Promise<{
  errorCode: string | null;
  outcome: SaveRegistrationOutcome;
}> {
  return await apiJson(
    `/reservations/${reservationId}/guests/${guestId}/registration`,
    {
      body: data,
      label: "Save guest registration",
      method: "POST",
      schema: SaveRegistrationResponseSchema,
      signal,
    }
  );
}
